import { subDays } from 'date-fns'
import type { UnitPreference } from '@/lib/conversions'
import type { LocationWithForecastAndReport } from '@/types/core'
import { convertSnowfall, convertTemp } from '@/lib/conversions'
import { supabase } from '@/auth/supabase'

/**
 * Convert forecast data from metric (mm/C) to user's preferred units
 * Report data is already in the correct units (inches/F) so it's left unchanged
 */
function convertForecastUnits(
  location: LocationWithForecastAndReport,
  units: UnitPreference = 'imperial',
): LocationWithForecastAndReport {
  if (!location.forecast) return location

  const forecast = location.forecast

  return {
    ...location,
    forecast: {
      ...forecast,
      forecastPrev12Hr: convertSnowfall(forecast.forecastPrev12Hr, units) ?? 0,
      forecastPrev24Hr: convertSnowfall(forecast.forecastPrev24Hr, units) ?? 0,
      forecast24Hr: convertSnowfall(forecast.forecast24Hr, units) ?? 0,
      forecast48Hr: convertSnowfall(forecast.forecast48Hr, units) ?? 0,
      forecast7Day: convertSnowfall(forecast.forecast7Day, units) ?? 0,
      forecastPeriods: (forecast.forecastPeriods ?? []).map((period) => ({
        ...period,
        summary: {
          snowTotal: convertSnowfall(period.summary.snowTotal, units),
          tempMin: convertTemp(period.summary.tempMin, units),
          tempMax: convertTemp(period.summary.tempMax, units),
        },
        hours: period.hours.map((hour) => ({
          ...hour,
          temp: convertTemp(hour.temp, units),
          snowfall: convertSnowfall(hour.snowfall, units),
        })),
      })),
    },
  }
}

// Fetch user's saved ski resorts with reports and forecasts
export async function fetchUserLocations() {
  // Get the current user
  const {
    data: { session },
    error: userError,
  } = await supabase.auth.getSession()

  if (userError || !session?.user) {
    console.error('Error getting user:', userError)
    throw new Error(`Couldnt authenticate user: Original: ${userError}`)
  }

  // just get some extra on the start for a buffer
  const currentDate = subDays(new Date(), 1).toISOString()

  // get current
  // Single query with aggregation
  const { data, error } = await supabase.rpc(
    'get_user_locations_forecasts_simple',
    {
      p_user_id: session.user.id,
      p_start_date: currentDate,
      p_use_imperial: true,
    },
  )

  if (error) {
    console.error('Error getting resorts with forecasts:', error)
    return { snotels: [], skiResorts: [], customLocations: [] }
  }

  const locationData = data as unknown as LocationWithForecastAndReport[]

  // Convert forecast data from metric (mm/C) to imperial (inches/F)
  // Report data is already in imperial units, so it's left unchanged
  const convertedLocations = locationData.map((loc) =>
    convertForecastUnits(loc, 'imperial'),
  )

  const snotels = convertedLocations.filter((loc) => loc.type === 'snotel')
  const skiResorts = convertedLocations.filter(
    (loc) => loc.type === 'ski_resort',
  )
  const customLocations = convertedLocations.filter(
    (loc) => loc.type === 'custom',
  )

  return { snotels, skiResorts, customLocations }
}

export async function fetchUserCustomLocations() {
  // Single query with aggregation
  const { data, error } = await supabase.from('user_custom_locations').select(`
      id,
      name,
      latitude,
      longitude,
      elevation,
      timezone,
      noaa_forecast_grid_id,
      state,
      avalanche_zone_id
      `)

  if (error) {
    console.error('Error getting custom locations:', error)
    throw new Error('Could not get your locations')
  }

  return data
}
